import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onGetStarted: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onGetStarted }) => {
  return (
    <section className="relative bg-gradient-to-br from-primary via-primary to-secondary overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-primary-foreground/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm font-medium text-primary-foreground">Trusted by 500+ Fleet Managers</span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight">
                Maximum Value,<br />
                <span className="text-accent">Minimum Hassle</span>
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/90 leading-relaxed max-w-xl">
                Skip the auction uncertainty. Get premium prices for your commercial fleet through our transparent, relationship-driven direct sales process.
              </p>
            </div>

            {/* Value Props */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
              { icon: 'ClockIcon', label: '24-48 Hour Valuations' },
              { icon: 'CurrencyDollarIcon', label: '15-25% Above Auction' },
              { icon: 'HandThumbUpIcon', label: 'Zero Hidden Fees' }].
              map((prop, index) =>
              <div key={index} className="flex items-center space-x-3 bg-primary-foreground/10 backdrop-blur-sm px-4 py-3 rounded-lg">
                  <Icon name={prop.icon as any} size={24} variant="outline" className="text-accent flex-shrink-0" />
                  <span className="text-sm font-medium text-primary-foreground">{prop.label}</span>
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={onGetStarted}
                className="px-8 py-4 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-1 shadow-brand flex items-center justify-center space-x-2">

                <span>Get Your Fleet Valued</span>
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
              </button>
              <Link
                href="/fleet-evaluation-process"
                className="px-8 py-4 bg-primary-foreground/10 backdrop-blur-sm text-primary-foreground font-semibold rounded-lg hover:bg-primary-foreground/20 transition-all duration-200 ease-out border border-primary-foreground/20 flex items-center justify-center space-x-2">

                <span>See How It Works</span>
                <Icon name="PlayIcon" size={20} variant="solid" />
              </Link>
            </div>

            {/* Urgency Indicator */}
            <div className="flex items-start space-x-3 bg-warning/20 backdrop-blur-sm px-4 py-3 rounded-lg border border-warning/30">
              <Icon name="BellAlertIcon" size={20} variant="solid" className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-primary-foreground">Need Immediate Liquidation?</p>
                <p className="text-xs text-primary-foreground/80 mt-1">Call our emergency line: <span className="font-bold">(555) 123-4567</span></p>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1281a8f72-1767057140980.png"
                alt="Professional utility trucks and work vehicles from company fleet parked at service facility ready for commercial operations"
                className="w-full h-[500px] object-cover" />

              {/* Overlay Stats */}
              <div className="absolute bottom-6 left-6 right-6 bg-card/95 backdrop-blur-sm rounded-xl p-6 shadow-brand">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">$1.6M+</p>
                    <p className="text-xs text-muted-foreground mt-1">Paid Last Year</p>
                  </div>
                  <div className="text-center border-l border-r border-border">
                    <p className="text-2xl font-bold text-primary">156</p>
                    <p className="text-xs text-muted-foreground mt-1">Vehicles Acquired</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">4.9★</p>
                    <p className="text-xs text-muted-foreground mt-1">Client Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;